import { useReadContracts } from 'wagmi'
import type { Address } from 'viem'
import { ADDRESSES, sipConfigured } from '../config/faucet.config'
import { erc20Abi, sipRouterAbi } from '../abi'

export interface SipReads {
  /** What one sip costs right now, in satoshi. Live from the Router. */
  sipSats?: bigint
  /** SEED one sip returns right now. Live from the Router. */
  seedOut?: bigint
  sipsPaused?: boolean
  /** SEED the Router currently holds — a sip larger than this cannot settle. */
  inventory?: bigint
  /** The caller's cbBTC allowance for the ROUTER (a separate spender from the faucet). */
  routerAllowance?: bigint
  refetch: () => void
}

/**
 * Everything the TAKE A SIP option shows, in one multicall. Entirely inert while
 * the Router address is unconfigured: no request is made and every field stays
 * undefined, which the page renders as the option simply not existing.
 *
 * `previewSip` can itself revert (a stale or invalid oracle); `allowFailure`
 * turns that into undefined fields, which the page renders as em-dashes and a
 * disabled button rather than a crash.
 */
export function useSipReads(address?: Address): SipReads {
  const router = { address: ADDRESSES.sipRouter, abi: sipRouterAbi } as const

  const { data, refetch } = useReadContracts({
    allowFailure: true,
    contracts: [
      { ...router, functionName: 'previewSip' },
      { ...router, functionName: 'paused' },
      { ...router, functionName: 'inventory' },
      {
        address: ADDRESSES.cbBTC,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address ?? '0x0000000000000000000000000000000000000000', ADDRESSES.sipRouter],
      },
    ],
    // 10s, matching the keeper's replenish cadence: once a refill confirms, the
    // inventory read catches it on the next tick and TAKE A SIP re-enables
    // without a page refresh.
    query: { enabled: sipConfigured, refetchInterval: 10_000 },
  })

  const preview = data?.[0]?.status === 'success' ? (data[0].result as readonly [bigint, bigint]) : undefined

  return {
    sipSats: preview?.[0],
    seedOut: preview?.[1],
    sipsPaused: data?.[1]?.status === 'success' ? (data[1].result as boolean) : undefined,
    inventory: data?.[2]?.status === 'success' ? (data[2].result as bigint) : undefined,
    routerAllowance: address && data?.[3]?.status === 'success' ? (data[3].result as bigint) : undefined,
    refetch: () => {
      void refetch()
    },
  }
}
